import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {AuthService} from './auth.service';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map, mergeMap, take} from 'rxjs/operators';
import {NGXLogger} from 'ngx-logger';
import {AUTHENTICATED_DOMAINS, environment} from '../../environments/environment';
import {API_PERSON_TOKEN} from './api.service';
import {SessionExpiryService, sessionExpiryCode} from './session-expiry.service';
import {SESSION_EXPIRED_REASON} from '../models/session-expired-error';

// case insensitive check against config and value
const startsWithAny = (arr: string[] = []) => (value = '') => {
  return arr.some(test => value.toLowerCase().startsWith(test.toLowerCase()));
};
const isAbsoluteURL = startsWithAny(['http', '//']);

// Gets the bearer too, but its 401 comes from its own rules, not from the session.
const NON_SESSION_HOSTS = ['firebasestorage.googleapis.com'];

// Only the backends' expired-session refusal: a missing claims header or a token
// the gateway cannot validate is a 401 too, and signing out for it would loop the
// user through the login under a false message.
const isSessionRejection = (error: unknown, request: HttpRequest<unknown>): error is HttpErrorResponse => {
  if (!(error instanceof HttpErrorResponse) || error.status !== 401 || !request.headers.has('Authorization')
    || sessionExpiryCode(error) !== SESSION_EXPIRED_REASON) {
    return false;
  }
  const url = new URL(request.url, location.origin);
  return url.pathname !== API_PERSON_TOKEN && !NON_SESSION_HOSTS.includes(url.hostname);
};

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private auth: AuthService,
              private logger: NGXLogger,
              private sessionExpiry: SessionExpiryService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let authenticated: boolean;
    if (isAbsoluteURL(request.url)) {
      const hostname = new URL(request.url).hostname;
      authenticated = AUTHENTICATED_DOMAINS.some(domain => hostname.includes(domain));
    } else {
      authenticated = true;
    }

    if (!authenticated || request.headers.has('Authorization')) {
      return next.handle(request);
    }

    request = request.clone({
      setParams: {
        key: environment.closecareApiKey
      }
    });

    return this.auth.token$.pipe(
      take(1),
      catchError((error) => {
        this.logger.error(error);
        // Sent without the bearer, so the caller gets the backend's answer
        // instead of a request that never goes out.
        return of(null);
      }),
      map((token) => {
        if (!!token) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            },
          });
        }
        return request;
      }),
      mergeMap((r) => next.handle(r).pipe(
        catchError((error) => isSessionRejection(error, r) ? this.sessionExpiry.handleUnauthorized(error) : throwError(error))
      ))
    );
  }
}
